import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  type StateStorage,
} from "zustand/middleware";

/** Which step list MainSendCashScreen uses — must survive reload (distinct from isNewConsumer). */
export type SendCashStepsMode = "existing" | "new" | "resetPin";

/** Which step list MainBillPayScreen uses — URL slug maps differently for NEW vs EXISTING. */
export type BillPayStepsMode = "existing" | "new";

/** Must match persist `name` — used to sync initial send step list on reload before React hydrates. */
export const QUICKSTAGE_SESSION_STORAGE_KEY = "quickstage-session";

/**
 * When leaving a flow via header X, we navigate to `/` first, then run the partial reset on Dashboard
 * mount. Otherwise resetting `sendCashStepsMode` / etc. while MainSendCashScreen is still mounted makes
 * `useWebFlowStepSync` push a new step slug (looks like “one step back”) before the route unmounts.
 */
export const PENDING_FLOW_CLOSE_RESET_SESSION_KEY = "quickstage-pending-flow-close-reset";

export function scheduleFlowCloseResetForDashboard(): void {
  try {
    globalThis.sessionStorage?.setItem(PENDING_FLOW_CLOSE_RESET_SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}

const SESSION_STORAGE_KEY = QUICKSTAGE_SESSION_STORAGE_KEY;

/** Web: read which send step array was active (survives page reload). No-op on non-web. */
export function getPersistedSendCashStepsMode(): SendCashStepsMode {
  try {
    const raw = (globalThis as { sessionStorage?: { getItem: (k: string) => string | null } })
      .sessionStorage?.getItem(QUICKSTAGE_SESSION_STORAGE_KEY);
    if (!raw) return "existing";
    const parsed = JSON.parse(raw) as { state?: { sendCashStepsMode?: SendCashStepsMode } };
    const mode = parsed?.state?.sendCashStepsMode;
    if (mode === "new" || mode === "existing" || mode === "resetPin") return mode;
  } catch {
    /* ignore */
  }
  return "existing";
}

/** Web: read which bill pay step array was active (survives page reload). */
export function getPersistedBillPayStepsMode(): BillPayStepsMode {
  try {
    const raw = (globalThis as { sessionStorage?: { getItem: (k: string) => string | null } })
      .sessionStorage?.getItem(QUICKSTAGE_SESSION_STORAGE_KEY);
    if (!raw) return "existing";
    const parsed = JSON.parse(raw) as { state?: { billPayStepsMode?: BillPayStepsMode } };
    const mode = parsed?.state?.billPayStepsMode;
    if (mode === "new" || mode === "existing") return mode;
  } catch {
    /* ignore */
  }
  return "existing";
}

const memoryFallback: Record<string, string> = {};
const noopWebStorage: StateStorage = {
  getItem: (name) => memoryFallback[name] ?? null,
  setItem: (name, value) => {
    memoryFallback[name] = value;
  },
  removeItem: (name) => {
    delete memoryFallback[name];
  },
};

function getSessionStorageAdapter(): StateStorage {
  try {
    const ss = (globalThis as { sessionStorage?: StateStorage }).sessionStorage;
    if (ss) return ss;
  } catch {
    /* ignore */
  }
  return noopWebStorage;
}

const IDENTIFIER_KEYS = {
  phoneNumber: "quickstage.identifier.phoneNumber",
  kioskId: "quickstage.identifier.kioskId",
  qrCodeNumber: "quickstage.identifier.qrCodeNumber"
} as const;

type IdentifierStorageKey =
  (typeof IDENTIFIER_KEYS)[keyof typeof IDENTIFIER_KEYS];

/** QR / kiosk context expires after 2 hours to avoid staging at the wrong store. */
export const QR_IDENTIFIER_TTL_MS = 2 * 60 * 60 * 1000;

interface StoredIdentifierEntry {
  value: string;
  expiresAt: number;
}

function getIdentifierTtlMs(key: IdentifierStorageKey): number | null {
  if (
    key === IDENTIFIER_KEYS.kioskId ||
    key === IDENTIFIER_KEYS.qrCodeNumber
  ) {
    return QR_IDENTIFIER_TTL_MS;
  }
  return null;
}

function parseStoredIdentifier(
  raw: string,
  key: IdentifierStorageKey,
): string | null {
  const ttlMs = getIdentifierTtlMs(key);

  try {
    const parsed = JSON.parse(raw) as StoredIdentifierEntry;
    if (
      parsed &&
      typeof parsed.value === "string" &&
      typeof parsed.expiresAt === "number"
    ) {
      if (ttlMs != null && Date.now() > parsed.expiresAt) {
        return null;
      }
      const trimmed = parsed.value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  } catch {
    /* fall through to legacy plain-string handling */
  }

  // Legacy plain-string QR entries (no expiry metadata) are treated as expired.
  if (ttlMs != null) {
    return null;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readIdentifierFromLocalStorage(key: IdentifierStorageKey): string | null {
  try {
    const ls = (globalThis as { localStorage?: { getItem: (k: string) => string | null; removeItem: (k: string) => void } }).localStorage;
    const value = ls?.getItem(key);
    if (!value) return null;
    const parsed = parseStoredIdentifier(value, key);
    if (parsed == null) {
      ls?.removeItem(key);
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeIdentifierToLocalStorage(key: IdentifierStorageKey, value: string | null | undefined): void {
  try {
    const ls = (globalThis as {
      localStorage?: {
        setItem: (k: string, v: string) => void;
        removeItem: (k: string) => void;
      };
    }).localStorage;
    if (!ls) return;
    const normalized = (value ?? "").toString().trim();
    if (normalized.length > 0) {
      const ttlMs = getIdentifierTtlMs(key);
      if (ttlMs != null) {
        const entry: StoredIdentifierEntry = {
          value: normalized,
          expiresAt: Date.now() + ttlMs,
        };
        ls.setItem(key, JSON.stringify(entry));
      } else {
        ls.setItem(key, normalized);
      }
    } else {
      ls.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

function getPersistentIdentifiers() {
  return {
    phoneNumber: readIdentifierFromLocalStorage(IDENTIFIER_KEYS.phoneNumber),
    kioskId: readIdentifierFromLocalStorage(IDENTIFIER_KEYS.kioskId),
    qrCodeNumber: readIdentifierFromLocalStorage(IDENTIFIER_KEYS.qrCodeNumber),
  };
}

/** Letters + digits only; strip spaces (e.g. "L3L 0B5" → "L3L0B5"). Max 10. */
function normalizePostalCode(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = value
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 10);
  return normalized || null;
}

export interface SessionState {
   // Transaction flows
  isReceiver?: boolean;
  isSendCash?: boolean;
  isBillPay?: boolean;
  isPromocodeApplied?: boolean;
  isPromoActivityIndicator?: boolean;
  selectedLanguage?: string;

  agentId: string | null;
  unitProfileId: string | null;
  selectedDestinationCountryCode: string | null;
  selectedDestinationCountryName: string | null;
  selectedDestinationStateAbbr: string | null;
  selectedDestinationStateName: string | null;
  selectedReceiveCurrency: string | null;
  selectedSendOption: any | null;
  pendingSendOption?: any | null;
  currentSendAmount?: string;
  theyReceiveAmount?: string;
  // Partner hierarchy details
  partnerName?: string | null;
  partnerAddress?: string | null;
  partnerCity?: string | null;
  partnerCountry?: string | null;
  partnerState?: string | null;
  partnerPostalCode?: string | null;
  receiverReceiveAmount?: string;
  referenceNumberSearchResult?: any | null;
  receiveAmountValidationError?: string | null;
  lastFeeLookupResponse?: any | null;
  // Store the params used for Send FeeLookup from Review/Amount entry
  sendFeeLookupReviewEditStore?: any | null;
  // Store selected delivery option from HowTheyReceive step
  selectedDeliveryOptionStore?: any | null;
  // GFFP derived data
  gffpCategories?: string[];
  gffpFieldsByCategory?: Record<string, any[]>;
  // Additional information capture when validation requires more fields
  missingRequiredFields?: any[]; // Fixed: ProductFieldInfo is not defined, use any[] or define the type elsewhere
  additionalFieldValues?: Record<string, any>;
  // Global loading state
  isLoading: boolean;
  loadingMessage: string;
  // Login identifiers
  phoneNumber?: string | null;
  consumerId?: string | null;
  consumerPin?: string | null;
  // Consumer lookup state
  isNewConsumer?: boolean;
  // Kiosk and location
  kioskId?: string | null;
  qrCodeNumber?: string | null; // The QR code scanned from URL (KID parameter)
  locationName?: string | null;
  mgiTransactionSessionId?: string | null;
  confirmationNumber?: string | null;
  // Sender profile fields collected across steps
  senderFirstName?: string | null;
  senderMiddleName?: string | null;
  senderLastName?: string | null;
  senderLastName2?: string | null;
  senderAddress?: string | null;
  senderCity?: string | null;
  senderZipCode?: string | null;
  // senderState is already represented by selectedStateAbbr/Name for different contexts; keep a direct field too
  senderState?: string | null;
  /** Sender address country (ISO3), collected on the legacy send address step. */
  senderCountry?: string | null;
  senderDOBString?: string | null; // MM/DD/YYYY from input
  senderDOB?: Date | null;
  RecieverDOBString?: string | null;
  RecieverDOB?: Date | null;
  senderPhotoIdNumber?: string | null;
  receiverPhotoIdNumber?: string | null;
  // Receiver profile fields collected across steps
  receiverFirstName?: string | null;
  receiverMiddleName?: string | null;
  receiverLastName?: string | null;
  receiverLastName2?: string | null;
  // RRN / Customer Receive Number captured when required
  customerReceiveNumber?: string | null;
  // Identification selection
  selectedIdTypeKey?: string | null;
  selectedIdTypeValue?: string | null;
  selectedIdTypeLabel?: string | null;
  selectedIdPhotoIdCountry?: string | null;
  selectedIdPhotoIdStateAbbr?: string | null;
  selectedIdPhotoIdStateName?: string | null;
  // Step timing
  currentStepKey?: string | null;
  stepStartTime?: number | null;
  recordedScreenDurations?: Array<{ screenName: string; durationMs: number }>;
  receiverAddress?: string | null;
  receiverCity?: string | null;
  receiverZipCode?: string | null;
  receiverState?: string | null;
  /** Receiver address country (ISO3), collected on the legacy receive address step. */
  receiverCountry?: string | null;

  // Bill Pay auth consumer response (for reuse across steps)
  billPayGetAuthConsumerResponseStore?: any | null;
  // Bill Pay selected biller info
  selectedBillerInformationStore?: any | null;

  // Selected biller account number
  billPayBillerAccountNumberStore?: string | null;
  // BillerSearch response cache
  billPayBillerSearchResponseStore?: any | null;
  // FeeLookup response cache for BillPay
  billPayFeeLookupResponseStore?: any | null;
  // GFFP response cache for BillPay
  billPayGFFPResponseStore?: any | null;
  // Bill Pay validation response cache
  billPayValidationResponseStore?: any | null;
  // Bill Pay all required/optional fields (flat list)
  billPayAllRequiredFieldsStore?: any[] | null;
  // Final send validation response
  finalSendValidationResponseStore?: any | null;

  /** Send cash: which static step array is active (URL slug maps differently for NEW vs EXISTING). */
  sendCashStepsMode?: SendCashStepsMode;

  /** Bill pay: existing vs new consumer step list. */
  billPayStepsMode?: BillPayStepsMode;

  // setters/actions
  setIsReceiver?: (isReceive: boolean) => void;
  setIsSendCash?: (isSend: boolean) => void;
  setIsBillPay?: (isBill: boolean) => void;
  setIsPromocodeApplied?: (isPromocode: boolean) => void;
  setIsPromoActivityIndicator?: (isPromoActivity: boolean) => void;
  setselectedLanguage?: (selectedLanguage: string) => void;
  setAgentId: (id: string | null) => void;
  setUnitProfileId: (id: string | null) => void;
  setSelectedDestinationCountryName: (code: string | null) => void;
  setSelectedDestinationCountryCode: (name: string | null) => void;
  setSelectedDestinationStateAbbr: (abbr: string | null) => void;
  setSelectedDestinationStateName: (name: string | null) => void;
  setSelectedReceiveCurrency: (currency: string | null) => void;
  setCurrentSendAmount?: (amount: string) => void;
  setTheyReceiveAmount?: (theyReceive: string) => void;
  setLastFeeLookupResponse?: (resp: any | null) => void;
  setSendFeeLookupReviewEditStore?: (params: any | null) => void;
  setSelectedDeliveryOptionStore?: (resp: any | null) => void;
  setGffpCategories?: (cats: string[]) => void;
  setGffpFieldsByCategory?: (map: Record<string, any[]>) => void;
  setMissingRequiredFields?: (fields: any[]) => void;
  setAdditionalFieldValue?: (key: string, value: any) => void;
  setAdditionalFieldValues?: (values: Record<string, any>) => void;
  // Loading actions
  setLoading: (loading: boolean, message?: string) => void;
  startLoading: (message?: string) => void;
  stopLoading: () => void;
  setSelectedSendOption: (fee: any | null) => void;
  setPendingSendOption?: (fee: any | null) => void;
  // Partner hierarchy setters
  setPartnerDetails?: (details: { name: string | null; address: string | null; city?: string | null; country?: string | null; state?: string | null; postalCode?: string | null }) => void;
  setSelectedIdTypeKey?: (idKey: string | null) => void;
  setSelectedIdTypeValue?: (value: string | null) => void;
  setSelectedIdTypeLabel?: (label: string | null) => void;
  setSelectedIdPhotoIdCountry?: (country: string | null) => void;
  setSelectedIdPhotoIdStateAbbr?: (state: string | null) => void;
  setSelectedIdPhotoIdStateName?: (state: string | null) => void;
  setKioskId?: (id: string | null) => void;
  setQrCodeNumber?: (qrCode: string | null) => void;
  setLocationName?: (name: string | null) => void;
  setMgiTransactionSessionId?: (id: string | null) => void;
  setConfirmationNumber?: (ref: string | null) => void;
  // Login setters
  setPhoneNumber?: (phone: string | null) => void;
  setConsumerId?: (id: string | null) => void;
  setConsumerPin?: (pin: string | null) => void;
  setIsNewConsumer?: (isNew: boolean) => void;
  // Sender setters
  setSenderFirstName?: (v: string | null) => void;
  setSenderMiddleName?: (v: string | null) => void;
  setSenderLastName?: (v: string | null) => void;
  setSenderLastName2?: (v: string | null) => void;
  setSenderAddress?: (v: string | null) => void;
  setRecieverDOBString?: (v: string | null) => void;
  setRecieverDOB?: (v: Date | null) => void;
  setSenderCity?: (v: string | null) => void;
  setSenderZipCode?: (v: string | null) => void;
  setSenderState?: (v: string | null) => void;
  setSenderCountry?: (v: string | null) => void;
  setSenderDOBString?: (v: string | null) => void;
  setSenderDOB?: (v: Date | null) => void;
  setSenderPhotoIdNumber?: (v: string | null) => void;
  setReceiverPhotoIdNumber?: (v: string | null) => void;
  // Receiver setters
  setReceiverFirstName?: (v: string | null) => void;
  setReceiverMiddleName?: (v: string | null) => void;
  setReceiverLastName?: (v: string | null) => void;
  setReceiverLastName2?: (v: string | null) => void;
  setCustomerReceiveNumber?: (v: string | null) => void;
  // timing actions
  recordStepTransition?: (nextStepKey: string) => void;
  setReceiverAddress?: (v: string | null) => void;
  setReceiverCity?: (v: string | null) => void;
  setReceiverZipCode?: (v: string | null) => void;
  setReceiverState?: (v: string | null) => void;
  setReceiverCountry?: (v: string | null) => void;
  setBillPayGetAuthConsumerResponseStore?: (resp: any | null) => void;
  setSelectedBillerInformationStore?: (resp: any | null) => void;
  setBillPayBillerAccountNumberStore?: (v: string | null) => void;
  setBillPayBillerSearchResponseStore?: (resp: any | null) => void;
  setBillPayFeeLookupResponseStore?: (resp: any | null) => void;
  setBillPayGFFPResponseStore?: (resp: any | null) => void;
  setBillPayValidationResponseStore?: (resp: any | null) => void;
  setBillPayAllRequiredFieldsStore?: (fields: any[] | null) => void;
  setFinalSendValidationResponseStore?: (resp: any | null) => void;
  setSendCashStepsMode?: (mode: SendCashStepsMode) => void;
  setBillPayStepsMode?: (mode: BillPayStepsMode) => void;
  reset: () => void;
  resetForClosePreservePhoneAndQr: () => void;
}

// keep a reference to the initial state
const initialPersistentIdentifiers = getPersistentIdentifiers();
const initialState = {
  isReceiver: false,
  isSendCash: false,
  isBillPay: false,
  isPromocodeApplied: false,
  isPromoActivityIndicator: false,
  selectedLanguage: 'english',

  // Not resetting agentId and unitProfileId
  // agentId: null,
  // unitProfileId: null,
  selectedDestinationCountryCode: null,
  selectedDestinationCountryName: null,
  selectedDestinationStateAbbr: null,
  selectedDestinationStateName: null,
  selectedReceiveCurrency: null,
  selectedSendOption: null,
  pendingSendOption: null,
  partnerName: null,
  partnerAddress: null,
  partnerCity: null,
  partnerCountry: null,
  partnerState: null,
  partnerPostalCode: null,
  currentSendAmount: '',
  theyReceiveAmount: '',
  receiverReceiveAmount: '',
  referenceNumberSearchResult: null,
  receiveAmountValidationError: null,
  lastFeeLookupResponse: null,
  sendFeeLookupReviewEditStore: null,
  selectedDeliveryOptionStore: null,
  // GFFP derived data
  gffpCategories: [],
  gffpFieldsByCategory: {},
  // Additional information capture when validation requires more fields
  missingRequiredFields: [], // Fixed: ProductFieldInfo is not defined, use any[] or define the type elsewhere
  additionalFieldValues: {},
  // Global loading state
  isLoading: false,
  loadingMessage: '',
  // Login identifiers
  phoneNumber: initialPersistentIdentifiers.phoneNumber,
  consumerId: null,
  consumerPin: null,
  // Consumer lookup state
  isNewConsumer: false,
  // Kiosk and location
  kioskId: initialPersistentIdentifiers.kioskId,
  qrCodeNumber: initialPersistentIdentifiers.qrCodeNumber,
  locationName: null,
  mgiTransactionSessionId: null,
  confirmationNumber: null,
  // Sender profile fields collected across steps
  senderFirstName: null,
  senderMiddleName: null,
  senderLastName: null,
  senderLastName2: null,
  senderAddress: null,
  senderCity: null,
  senderZipCode: null,
  // senderState is already represented by selectedStateAbbr/Name for different contexts; keep a direct field too
  senderState: null,
  senderCountry: null,
  senderDOBString: null,
  senderDOB: null,
  RecieverDOBString: null,
  RecieverDOB: null,
  senderPhotoIdNumber: null,
  receiverPhotoIdNumber: null,
  // Receiver profile fields collected across steps
  receiverFirstName: null,
  receiverMiddleName: null,
  receiverLastName: null,
  receiverLastName2: null,
  // RRN / Customer Receive Number captured when required
  customerReceiveNumber: null,
  // Identification selection
  selectedIdTypeKey: null,
  selectedIdTypeValue: null,
  selectedIdTypeLabel: null,
  selectedIdPhotoIdCountry: null,
  selectedIdPhotoIdStateAbbr: null,
  selectedIdPhotoIdStateName: null,
  // Step timing
  currentStepKey: null,
  stepStartTime: null,
  recordedScreenDurations: [],
  receiverAddress: null,
  receiverCity: null,
  receiverZipCode: null,
  receiverState: null,
  receiverCountry: null,

  // Bill Pay auth consumer response (for reuse across steps)
  billPayGetAuthConsumerResponseStore: null,
  // Bill Pay selected biller info
  selectedBillerInformationStore: null,

  // Selected biller account number
  billPayBillerAccountNumberStore: null,
    // BillerSearch response cache
  billPayBillerSearchResponseStore: null,
  // FeeLookup response cache for BillPay
  billPayFeeLookupResponseStore: null,
  // GFFP response cache for BillPay
  billPayGFFPResponseStore: null,
  billPayValidationResponseStore: null,
  billPayAllRequiredFieldsStore: null,
  finalSendValidationResponseStore: null,
  sendCashStepsMode: "existing" as SendCashStepsMode,
  billPayStepsMode: "existing" as BillPayStepsMode,
};


export const createSessionStore = () => {
  const persistedIdentifiers = getPersistentIdentifiers();
  const store = create<SessionState>()(
    persist(
      (set, get) => ({
    isReceiver: false,
    isSendCash: false,
    isBillPay: false,
    isPromocodeApplied: false,
    isPromoActivityIndicator: false,
    agentId: null,
    unitProfileId: null,
    selectedDestinationCountryCode: null,
    selectedDestinationCountryName: null,
    selectedDestinationStateAbbr: null,
    selectedDestinationStateName: null,
    selectedReceiveCurrency: null,
    selectedSendOption: null,
    pendingSendOption: null,
    partnerName: null,
    partnerAddress: null,
    partnerCity: null,
    partnerCountry: null,
    partnerState: null,
    partnerPostalCode: null,
    currentSendAmount: '',
    theyReceiveAmount: '',
    receiverReceiveAmount: '',
    referenceNumberSearchResult: null,
    receiveAmountValidationError: null,
    lastFeeLookupResponse: null,
    sendFeeLookupReviewEditStore: null,
    gffpCategories: [],
    gffpFieldsByCategory: {},
    missingRequiredFields: [],
    additionalFieldValues: {},
    isLoading: false,
    loadingMessage: "Loading...",
    phoneNumber: persistedIdentifiers.phoneNumber,
    consumerId: null,
    consumerPin: null,
    isNewConsumer: false,
    kioskId: persistedIdentifiers.kioskId,
    qrCodeNumber: persistedIdentifiers.qrCodeNumber,
    locationName: null,
    mgiTransactionSessionId: null,
    confirmationNumber: null,
    billPayGetAuthConsumerResponseStore: null,
    selectedBillerInformationStore: null,
    billPayBillerAccountNumberStore: null,
    billPayBillerSearchResponseStore: null,
    billPayFeeLookupResponseStore: null,
    billPayGFFPResponseStore: null,
    billPayValidationResponseStore: null,
    billPayAllRequiredFieldsStore: null,
    finalSendValidationResponseStore: null,
    sendCashStepsMode: "existing",
    billPayStepsMode: "existing",
    senderFirstName: null,
    senderMiddleName: null,
    senderLastName: null,
    senderLastName2: null,
    senderAddress: null,
    senderCity: null,
    senderZipCode: null,
    senderState: null,
    senderCountry: null,
    senderDOBString: null,
    senderDOB: null,
    senderPhotoIdNumber: null,
    receiverPhotoIdNumber: null,
    receiverFirstName: null,
    receiverMiddleName: null,
    receiverLastName: null,
    receiverLastName2: null,
    customerReceiveNumber: null,
    selectedIdTypeKey: null,
    selectedIdTypeValue: null,
    selectedIdTypeLabel: null,
    selectedIdPhotoIdCountry: null,
    selectedIdPhotoIdState: null,
    RecieverDOBString: null,
    RecieverDOB: null,
    currentStepKey: null,
    stepStartTime: null,
    recordedScreenDurations: [],
    setIsReceiver: (isReceive: boolean) => set({ isReceiver: isReceive }),
    setIsSendCash: (isSend: boolean) => set({ isSendCash: isSend }),
    setIsBillPay: (isBill: boolean) => set({ isBillPay: isBill }),
    setIsPromocodeApplied: (isPromocode: boolean) => set({ isPromocodeApplied: isPromocode}),
    setIsPromoActivityIndicator: (isPromoActivity: boolean) => set({isPromoActivityIndicator: isPromoActivity}),
    setselectedLanguage: (selectLanguage: string) => set({ selectedLanguage: selectLanguage }),
    setAgentId: (id) => set({ agentId: id }),
    setUnitProfileId: (id) => set({ unitProfileId: id }),
    setSelectedDestinationCountryCode: (code) => set({ selectedDestinationCountryCode: code }),
    setSelectedDestinationCountryName: (name) => set({ selectedDestinationCountryName: name }), 
    setSelectedDestinationStateAbbr: (abbr) => set({ selectedDestinationStateAbbr: abbr }),
    setSelectedDestinationStateName: (name) => set({ selectedDestinationStateName: name }),
    setSelectedReceiveCurrency: (currency) => set({ selectedReceiveCurrency: currency }),
    setCurrentSendAmount: (amount) => set({ currentSendAmount: amount }),
    setTheyReceiveAmount: (theyreceive) => set({ theyReceiveAmount: theyreceive}),
    setReceiverReceiveAmount: (receiverreceive) => set({ receiverReceiveAmount: receiverreceive}),
    setReceiveAmountValidationError: (msg) => set({ receiveAmountValidationError: msg }),
    setReferenceNumberSearchResult: (resp) => set({ referenceNumberSearchResult: resp }),
    setLastFeeLookupResponse: (resp) => set({ lastFeeLookupResponse: resp }),
    setSendFeeLookupReviewEditStore: (params) => set({ sendFeeLookupReviewEditStore: params }),
    setSelectedDeliveryOptionStore: (resp) => set({ selectedDeliveryOptionStore: resp }),
    setGffpCategories: (cats) => set({ gffpCategories: cats }),
    setGffpFieldsByCategory: (map) => set({ gffpFieldsByCategory: map }),
    setMissingRequiredFields: (fields) => set({ missingRequiredFields: fields || [] }),
    setAdditionalFieldValue: (key, value) => set({ additionalFieldValues: { ...(get().additionalFieldValues || {}), [key]: value } }),
    setAdditionalFieldValues: (values) => set({ additionalFieldValues: values || {} }),
    setLoading: (loading, message = "Loading...") => set({ isLoading: loading, loadingMessage: message }),
    startLoading: (message = "Loading...") => set({ isLoading: true, loadingMessage: message }),
    stopLoading: () => set({ isLoading: false, loadingMessage: "Loading..." }),
    setSelectedSendOption: (fee) => set({ selectedSendOption: fee }),
    setPendingSendOption: (fee) => set({ pendingSendOption: fee })
    ,
    setPartnerDetails: (details) => set({ 
      partnerName: details?.name ?? null, 
      partnerAddress: details?.address ?? null,
      partnerCity: details?.city ?? null,
      partnerCountry: details?.country ?? null,
      partnerState: details?.state ?? null,
      partnerPostalCode: details?.postalCode ?? null
    })
    ,
    // Identification selection
    setSelectedIdTypeKey: (idKey: string | null) => set({ selectedIdTypeKey: idKey }),
    setSelectedIdTypeValue: (value: string | null) => set({ selectedIdTypeValue: value }),
    setSelectedIdTypeLabel: (label: string | null) => set({ selectedIdTypeLabel: label }),
    setSelectedIdPhotoIdCountry: (country: string | null) => set({ selectedIdPhotoIdCountry: country }),
    setSelectedIdPhotoIdStateAbbr: (state: string | null) => set({ selectedIdPhotoIdStateAbbr: state }),
    setSelectedIdPhotoIdStateName: (state: string | null) => set({ selectedIdPhotoIdStateName: state }),
    // Kiosk and location setters
    setKioskId: (id) => {
      writeIdentifierToLocalStorage(IDENTIFIER_KEYS.kioskId, id);
      set({ kioskId: id });
    },
    setQrCodeNumber: (qrCode) => {
      writeIdentifierToLocalStorage(IDENTIFIER_KEYS.qrCodeNumber, qrCode);
      set({ qrCodeNumber: qrCode });
    },
    setLocationName: (name) => set({ locationName: name }),
    setMgiTransactionSessionId: (id) => set({ mgiTransactionSessionId: id }),
    setConfirmationNumber: (ref) => set({ confirmationNumber: ref }),
    // Login setters
    setPhoneNumber: (phone) => {
      writeIdentifierToLocalStorage(IDENTIFIER_KEYS.phoneNumber, phone);
      set({ phoneNumber: phone });
    },
    setConsumerId: (id) => set({ consumerId: id }),
    setConsumerPin: (pin) => set({ consumerPin: pin }),
    setIsNewConsumer: (isNew: boolean) => set({ isNewConsumer: isNew }),
    // Sender setters
    setSenderFirstName: (v) => set({ senderFirstName: v }),
    setSenderMiddleName: (v) => set({ senderMiddleName: v }),
    setSenderLastName: (v) => set({ senderLastName: v }),
    setSenderLastName2: (v) => set({ senderLastName2: v }),
    setSenderAddress: (v) => set({ senderAddress: v }),
    setSenderCity: (v) => set({ senderCity: v }),
    setSenderZipCode: (v) => set({ senderZipCode: normalizePostalCode(v) }),
    setSenderState: (v) => set({ senderState: v }),
    setSenderCountry: (v) => set({ senderCountry: v }),
    setSenderDOBString: (v) => set({ senderDOBString: v }),
    setSenderDOB: (v) => set({ senderDOB: v }),
    setSenderPhotoIdNumber: (v) => set({ senderPhotoIdNumber: v }),
    setReceiverPhotoIdNumber: (v) => set({ receiverPhotoIdNumber: v }),
    // Receiver setters
    setRecieverDOBString: (v) => set({ RecieverDOBString: v }),
    setRecieverDOB: (v) => set({ RecieverDOB: v }),
    setReceiverFirstName: (v) => set({ receiverFirstName: v }),
    setReceiverMiddleName: (v) => set({ receiverMiddleName: v }),
    setReceiverLastName: (v) => set({ receiverLastName: v }),
    setReceiverLastName2: (v) => set({ receiverLastName2: v }),
    setCustomerReceiveNumber: (v) => set({ customerReceiveNumber: v }),
    setReceiverAddress: (v) => set({ receiverAddress: v }),
    setReceiverCity: (v) => set({ receiverCity: v }),
    setReceiverZipCode: (v) => set({ receiverZipCode: normalizePostalCode(v) }),
    setReceiverState: (v) => set({ receiverState: v }),
    setReceiverCountry: (v) => set({ receiverCountry: v }),
    setBillPayGetAuthConsumerResponseStore: (resp) => set({ billPayGetAuthConsumerResponseStore: resp }),
    setSelectedBillerInformationStore: (resp) => set({ selectedBillerInformationStore: resp }),
    setBillPayBillerAccountNumberStore: (v) => set({ billPayBillerAccountNumberStore: v }),
    setBillPayBillerSearchResponseStore: (resp) => set({ billPayBillerSearchResponseStore: resp }),
    setBillPayFeeLookupResponseStore: (resp) => set({ billPayFeeLookupResponseStore: resp }),
    setBillPayGFFPResponseStore: (resp) => set({ billPayGFFPResponseStore: resp }),
    setBillPayValidationResponseStore: (resp) => set({ billPayValidationResponseStore: resp }),
    setBillPayAllRequiredFieldsStore: (resp) => set({ billPayAllRequiredFieldsStore: resp }),
    setFinalSendValidationResponseStore: (resp) => set({ finalSendValidationResponseStore: resp }),
    setSendCashStepsMode: (mode) => set({ sendCashStepsMode: mode }),
    setBillPayStepsMode: (mode) => set({ billPayStepsMode: mode }),
    // timing actions
    recordStepTransition: (nextStepKey: string) => {
      const now = Date.now();
      const { currentStepKey, stepStartTime, recordedScreenDurations } = get();
      if (currentStepKey && stepStartTime) {
        const durationMs = Math.max(0, now - stepStartTime);
        set({
          recordedScreenDurations: [
            ...(recordedScreenDurations || []),
            { screenName: currentStepKey, durationMs }
          ]
        });
      }
      set({ currentStepKey: nextStepKey, stepStartTime: now });
    },
    reset: () => {
      const persistentIdentifiers = getPersistentIdentifiers();
      const selectedLanguage =
        get().selectedLanguage ?? initialState.selectedLanguage;
      set({
        ...initialState,
        agentId: null,
        unitProfileId: null,
        phoneNumber: persistentIdentifiers.phoneNumber,
        kioskId: persistentIdentifiers.kioskId,
        qrCodeNumber: persistentIdentifiers.qrCodeNumber,
        selectedLanguage
      });
      Promise.resolve().then(() => {
        try {
          store.persist.clearStorage();
        } catch {
          /* ignore */
        }
      });
    },
    resetForClosePreservePhoneAndQr: () => {
      const phoneNumber = get().phoneNumber ?? readIdentifierFromLocalStorage(IDENTIFIER_KEYS.phoneNumber);
      const kioskId = get().kioskId ?? readIdentifierFromLocalStorage(IDENTIFIER_KEYS.kioskId);
      const qrCodeNumber = get().qrCodeNumber ?? readIdentifierFromLocalStorage(IDENTIFIER_KEYS.qrCodeNumber);
      const selectedLanguage = get().selectedLanguage ?? initialState.selectedLanguage;
      writeIdentifierToLocalStorage(IDENTIFIER_KEYS.phoneNumber, phoneNumber);
      writeIdentifierToLocalStorage(IDENTIFIER_KEYS.kioskId, kioskId);
      writeIdentifierToLocalStorage(IDENTIFIER_KEYS.qrCodeNumber, qrCodeNumber);
      set({
        ...initialState,
        phoneNumber,
        kioskId,
        qrCodeNumber,
        selectedLanguage,
      });
    },
      }),
      {
        name: SESSION_STORAGE_KEY,
        storage: createJSONStorage(() => getSessionStorageAdapter()),
        partialize: (state) => {
          const { isLoading, loadingMessage, ...rest } = state;
          return rest as SessionState;
        },
        version: 1,
      }
    )
  );
  return store;
};

// Default export (plain store, no devtools)
export const useSessionStore = createSessionStore();

/** Call once when Dashboard mounts after closing a flow from `/send|receive|billPay/...`. */
export function consumePendingFlowCloseResetIfNeeded(): void {
  try {
    const ss = globalThis.sessionStorage;
    if (!ss || ss.getItem(PENDING_FLOW_CLOSE_RESET_SESSION_KEY) !== "1") return;
    ss.removeItem(PENDING_FLOW_CLOSE_RESET_SESSION_KEY);
    useSessionStore.getState().resetForClosePreservePhoneAndQr?.();
  } catch {
    /* ignore */
  }
}